import { useState } from 'react'
import { sileo } from 'sileo'

/**
 * Componente de formulario para la edición del nombre de un estado existente.
 * * * **Funcionalidades principales**:
 * - Validación de campos obligatorios.
 * - Prevención de peticiones innecesarias si el nombre no ha cambiado (Dirty checking).
 * - Feedback visual mediante notificaciones `sileo`.
 * - Gestión de estado de carga para evitar múltiples envíos.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.status - Objeto que representa el estado a editar.
 * @param {string|number} props.status.id - Identificador único del estado.
 * @param {string} props.status.name - Nombre actual del estado.
 * @param {Function} props.onSave - Función callback asíncrona que maneja la persistencia.
 * Recibe `(id, { name })`.
 * * @returns {JSX.Element} Un formulario con un campo de texto enfocado automáticamente.
 */
const StatusEdit = ({ status, onSave }) => {
  /** @type {[string, Function]} Estado local para controlar el valor del input */
  const [name, setName] = useState(status.name)

  /** @type {[boolean, Function]} Estado para controlar el spinner de carga y deshabilitar el botón */
  const [loading, setLoading] = useState(false)

  /**
   * Procesa el envío del formulario.
   * * Valida que el nombre no esté vacío y que sea diferente al valor original
   * antes de ejecutar el proceso de guardado.
   * * @param {React.FormEvent} e - Evento de envío nativo de React.
   */
  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validar que el nombre no contenga solo espacios
    if (!name.trim()) {
      return sileo.warning({
        title: 'Campo requerido',
        description: 'El nombre no puede estar vacío.',
      })
    }

    // Verificar si realmente hubo cambios antes de proceder
    if (name.trim() === status.name) {
      return sileo.info({
        title: 'Sin cambios',
        description: 'No modificaste nada.',
      })
    }

    try {
      setLoading(true)
      // Ejecutar la función de guardado pasada por props
      await onSave(status.id, { name: name.trim() })
    } catch (err) {
      console.error(err)
      sileo.error({
        title: 'Error',
        description: 'No se pudo guardar.',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <label className="se-label" htmlFor="statusEditName">
        Nombre del estado
      </label>
      <input
        id="statusEditName"
        type="text"
        className="form-control form-control-sm"
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
      />

      <div className="se-actions">
        <button className="btn btn-sm btn-primary" disabled={loading}>
          {loading ? (
            <span className="spinner-border spinner-border-sm me-2" />
          ) : (
            <i className="fa-solid fa-check me-2" />
          )}
          {loading ? 'Guardando...' : 'Guardar cambios'}
        </button>
      </div>

      <style>{`
        .se-label {
          display: block; margin-bottom: 4px; font-size: 0.72rem; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.04em; color: var(--gm-text-muted);
        }
        .se-actions { display: flex; justify-content: flex-end; gap: 6px; margin-top: 14px; }
      `}</style>
    </form>
  )
}

export default StatusEdit
